import { decode } from "html-entities";
import { addDays } from "date-fns";
import { default as _shuffle } from "lodash/shuffle.js";

export function formatTag(str) {
  return str.replace(/_/g, " ");
}
export function sixDecimalPlaces(str) {
  return Number(str).toFixed(6);
}

export function capitalizeWords(str) {
  // guard empty string
  if (!str || str.length === 0) return;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export function orderDaysOfWeek(daysObject) {
  const daysOrder = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const orderedDays = Object.entries(daysObject)
    .sort((a, b) => daysOrder.indexOf(a[0]) - daysOrder.indexOf(b[0]))
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});

  return orderedDays;
}
export function formatHtmlText(str) {
  // remove new lines
  return str.replace(/\n/g, "");
}
export function getUrlSlug(title) {
  // decode html entities
  return (
    decode(title)
      .toLowerCase()
      // fix ampersand/and
      .replace(/&/g, " and ")
      // fix apostrophes
      .replace(/'/g, "")
      // fix of other characters and put dashes
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
  );
}
export function groupByWithOrder(array, keyFn) {
  const groups = {};
  const keysInOrder = [];
  for (const item of array) {
    const key = keyFn(item);
    if (!groups.hasOwnProperty(key)) {
      groups[key] = [];
      keysInOrder.push(key);
    }
    groups[key].push(item);
  }
  let groupResults = [];
  for (const key of keysInOrder) {
    groupResults.push(groups[key]);
  }
  return groupResults;
}

export function getWeekendDays() {
  // sunday: 0,
  // monday: 1,
  // tuesday: 2,
  // wednesday: 3,
  // thursday: 4,
  // friday: 5,
  // saturday: 6,
  const today = new Date();
  const currentDay = today.getDay();
  const startDay = currentDay === 6 ? 6 : 5;
  const endDay = 0;
  // Days until next startWeekday (0 means today if matching)
  const daysToStart = (startDay - currentDay + 7) % 7;
  const nextStart = addDays(today, daysToStart);

  // Days from nextStart to nextEnd
  const startDayOfWeek = nextStart.getDay();
  const daysToEnd = (endDay - startDayOfWeek + 7) % 7;
  // Ensure at least 0 (same day), or strictly after (use || 7 as above)
  const nextEnd = addDays(nextStart, daysToEnd);

  return [nextStart, nextEnd];
}

export function scrollToFeed(isActive, isInstant) {
  if (isActive && typeof window !== "undefined") {
    const listingsHeader = document
      .querySelector("#fun-listings-header")
      ?.getBoundingClientRect();
    const navbar = document
      .querySelector("#fun-navbar")
      ?.getBoundingClientRect();
    const listingsBar = document
      .querySelector('[fun-id="fun-listings-bar"]')
      ?.getBoundingClientRect();
    // guard
    if (!navbar || !listingsBar) return;
    // instant scroll to position
    if (isInstant) {
      // +3 small adjustment
      window.scroll(
        0,
        navbar.height - listingsBar.height + listingsHeader.height + 3,
      );
      return;
    }
    // smooth scroll to position
    window.scrollTo({
      top: listingsBar.top + window.scrollY - navbar.height,
      left: 0,
      behavior: "smooth",
    });
  }
}
// Create typesense filter by string
export function createTypesenseFilterBy(appStore, filters) {
  let filterBy = "";

  // Geolocation
  if (
    filters.isFilterGeo &&
    appStore.geolocation.radius &&
    appStore.geolocation.latitude &&
    appStore.geolocation.longitude
  ) {
    filterBy += `location:(${appStore.geolocation.latitude}, ${appStore.geolocation.longitude}, ${appStore.geolocation.radius || "1"} mi)`;
  }

  // Date by UNIX timestamp
  if (filters.isFilterDate && appStore.typesense.active_start_time_unix) {
    filterBy += filterBy.length > 0 ? " && " : "";
    filterBy += `active_start_time_unix:=[${appStore.typesense.active_start_time_unix}]`;
  }
  if (filters.isFilterDate && appStore.typesense.active_end_time_unix) {
    filterBy += filterBy.length > 0 ? " && " : "";
    filterBy += `active_end_time_unix:=[${appStore.typesense.active_end_time_unix}]`;
  }

  // Listing Type Tags
  if (filters.isFilterTypeTags && appStore.typesense.filters.type.length > 0) {
    filterBy += filterBy.length > 0 ? " && " : "";
    let listingTypes = JSON.stringify(
      appStore.typesense.filters.type,
    ).replaceAll('"', "");
    filterBy += `type:${listingTypes}`;
  }

  // Crowd Tags
  if (
    filters.isFilterCrowdTags &&
    appStore.typesense.filters.crowd_tags.length > 0
  ) {
    filterBy += filterBy.length > 0 ? " && " : "";
    let crowdTags = JSON.stringify(
      appStore.typesense.filters.crowd_tags,
    ).replaceAll('"', "");
    filterBy += `crowd_tags:${crowdTags}`;
  }
  // Price Tags
  if (
    filters.isFilterPriceTags &&
    appStore.typesense.filters.price_tags.length > 0
  ) {
    filterBy += filterBy.length > 0 ? " && " : "";
    let priceTags = JSON.stringify(
      appStore.typesense.filters.price_tags,
    ).replaceAll('"', "");
    filterBy += `price_tags:${priceTags}`;
  }
  // Interest Tags
  if (
    filters.isFilterInterestTags &&
    appStore.typesense.filters.interest_tags.length > 0
  ) {
    filterBy += filterBy.length > 0 ? " && " : "";
    let interestTags = JSON.stringify(
      appStore.typesense.filters.interest_tags,
    ).replaceAll('"', "");
    filterBy += `interest_tags:${interestTags}`;
  }
  return filterBy;
}

// Create listing feed
export function createListingsFeed(listings) {
  if (!listings || !listings.length) return [];

  // Define the order of types we want to alternate
  const typeOrder = ["event", "activity", "bar", "restaurant"];

  // Group listings by type
  const listingsByType = {
    event: [],
    activity: [],
    bar: [],
    restaurant: [],
  };

  // Categorize listings by their type
  listings.forEach((listing) => {
    if (typeOrder.includes(listing.type)) {
      listingsByType[listing.type].push({ ...listing });
    }
  });

  // Create result array with alternating types
  const listingsFeed = [];
  let typeIndex = 0;
  let allTypesEmpty = false;

  while (!allTypesEmpty) {
    allTypesEmpty = true;

    // Try each type in sequence until we've gone through all types
    for (let i = 0; i < typeOrder.length; i++) {
      // Get current type in the rotation
      const currentType = typeOrder[(typeIndex + i) % typeOrder.length];

      // If we have listings of this type, add the next one
      if (listingsByType[currentType].length > 0) {
        const nextListing = listingsByType[currentType].shift();
        listingsFeed.push(nextListing);
        allTypesEmpty = false;

        // Move to the next type in the rotation
        typeIndex = (typeIndex + i + 1) % typeOrder.length;
        break;
      }
    }
  }

  return listingsFeed;
}
// Create a random ranking
export function randomRankListings(listings) {
  if (!listings || !listings.length) return [];

  // Define the order of types we want to alternate
  const typeOrder = ["event", "activity", "bar", "restaurant"];

  // Group listings by type
  const listingsByType = {
    event: [],
    activity: [],
    bar: [],
    restaurant: [],
  };

  // Randomize all listings then Categorize listings by their type
  _shuffle(listings).forEach((listing) => {
    if (typeOrder.includes(listing.type)) {
      listingsByType[listing.type].push({ ...listing });
    }
  });

  // Create result array with alternating types
  const result = [];
  let currentRank = 1;
  let typeIndex = 0;
  let allTypesEmpty = false;

  while (!allTypesEmpty) {
    allTypesEmpty = true;

    // Try each type in sequence until we've gone through all types
    for (let i = 0; i < typeOrder.length; i++) {
      // Get current type in the rotation
      const currentType = typeOrder[(typeIndex + i) % typeOrder.length];

      // If we have listings of this type, add the next one
      if (listingsByType[currentType].length > 0) {
        const nextListing = listingsByType[currentType].shift();
        nextListing.random_ranking = currentRank++;
        result.push(nextListing);
        allTypesEmpty = false;

        // Move to the next type in the rotation
        typeIndex = (typeIndex + i + 1) % typeOrder.length;
        break;
      }
    }
  }

  return result;
}
